import React, { PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";
import MuiDialog, { DialogClasses } from "@mui/material/Dialog";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { AtomProps } from "@/uiKit/atoms/utils/AtomTypes.ts";

type MaxWidth = "xs" | "sm" | "md" | "lg" | "xl" | number | undefined | false;

export type BaseDialogProps = PropsWithChildren<{
  open: boolean;
  role?: "dialog" | "alertdialog";
  fitContent?: boolean;
  maxWidth?: MaxWidth;
  onClose?: () => void;
  onClick?: () => void;
}>;

export type DialogProps = AtomProps<BaseDialogProps, Partial<DialogClasses>>;

export const Dialog: React.FC<DialogProps> = ({
  id,
  open,
  role = "dialog",
  fitContent = false,
  maxWidth = false,
  onClose,
  onClick,
  className,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles, maxWidth);

  return (
    <MuiDialog
      id={id}
      open={open}
      role={role}
      fullWidth={!fitContent}
      onClose={onClose}
      className={cx(styles.root, className)}
      classes={{
        ...classes,
        paper: cx(styles.paper, classes?.paper),
      }}
      onClick={e => {
        e.stopPropagation();
        if (onClick) {
          onClick();
        }
      }}
      slotProps={{
        backdrop: {
          onClick: e => {
            e.stopPropagation();
            if (onClose) {
              onClose();
            }
          },
        },
      }}
      {...props}
    />
  );
};

const makeStyles = (theme: Theme, maxWidth: MaxWidth) => {
  const maxWidthValue = !maxWidth
    ? `90vw`
    : typeof maxWidth === "number"
      ? `${maxWidth}px`
      : `${theme.breakpoints.values[maxWidth]}px`;

  return {
    root: css({}),
    paper: css({
      borderRadius: theme.spacing(16),
      maxWidth: `min(90vw, ${maxWidthValue})`,
    }),
  };
};
